import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { filter, finalize, switchMap, tap } from "rxjs";
import {
    ConfirmDialogComponent,
    ConfirmDialogData,
} from "../../../../../shared/_components/confirm-dialog/confirm-dialog.component";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";
import {
    BrokerAccountsService,
    LockTradingResult,
    TradingLockTarget,
    UnlockTradingResult,
} from "../../_services/broker-accounts.service";
import {
    TradingLockDialogComponent,
    TradingLockDialogData,
} from "../trading-lock-dialog/trading-lock-dialog.component";
import { Bot, BrokerAccount } from "../users.service";

// The API has no read of a lock, so a lock's state is known only from this view's own lock and unlock answers.
type TradingLockState = { kind: "unknown" } | { kind: "locked"; lockedSinceUtc: string | null } | { kind: "unlocked" };
export type TradingLockKind = TradingLockState["kind"];

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "app-trading-lock",
    imports: [DatePipe, MatButtonModule, MatIconModule],
    templateUrl: "./trading-lock.component.html",
    styleUrl: "./trading-lock.component.scss",
})
export class TradingLockComponent {
    readonly brokerAccount = input.required<BrokerAccount>();
    // Without a bot, the lock is the whole broker account's.
    readonly bot = input<Bot>();
    // Only a bot row gets it, as the dialog last saw its broker account: a bot that is not locked on its own trades
    // only while its account is not locked either.
    readonly accountLockKind = input<TradingLockKind>();
    readonly stateChange = output<TradingLockKind>();

    private readonly brokerAccountsService = inject(BrokerAccountsService);
    private readonly dialog = inject(MatDialog);
    private readonly notificationsService = inject(NotificationsService);
    private readonly destroyRef = inject(DestroyRef);

    readonly state = signal<TradingLockState>({ kind: "unknown" });
    readonly busy = signal(false);
    readonly targetName = computed(() => this.bot()?.name ?? this.brokerAccount().displayName);
    readonly freeToTrade = computed(
        () => this.state().kind === "unlocked" && (this.bot() === undefined || this.accountLockKind() === "unlocked"),
    );
    private readonly target = computed<TradingLockTarget>(() => ({
        brokerAccountId: this.brokerAccount().id,
        botId: this.bot()?.id,
    }));

    // The requests below are not tied to this view's lifetime: closing the user dialog must not abort a lock or an
    // unlock already sent, and its outcome is still shown.
    lock(): void {
        if (this.busy()) {
            return;
        }

        const data: TradingLockDialogData = {
            brokerAccountName: this.brokerAccount().displayName,
            botName: this.bot()?.name,
        };

        this.dialog
            .open<TradingLockDialogComponent, TradingLockDialogData, string>(TradingLockDialogComponent, {
                data,
                width: "95vw",
                maxWidth: "520px",
            })
            .afterClosed()
            .pipe(
                filter((reason): reason is string => reason !== undefined),
                tap(() => this.busy.set(true)),
                switchMap((reason) =>
                    this.brokerAccountsService
                        .lockTrading(this.target(), reason)
                        .pipe(finalize(() => this.busy.set(false))),
                ),
            )
            .subscribe({
                next: (result) => this.showLockResult(result),
                error: () =>
                    this.showFailure("The lock request failed, so the lock state is unknown. Locking again is safe."),
            });
    }

    unlock(): void {
        if (this.busy()) {
            return;
        }

        const data: ConfirmDialogData = {
            title: "Unlock trading",
            message: this.bot()
                ? `Unlock trading for bot "${this.targetName()}"? It trades again from its next scheduled run, ` +
                  "unless its broker account is locked. Nothing missed is caught up. The unlock is posted to the " +
                  "Service group."
                : `Unlock trading for broker account "${this.targetName()}"? Its bots resume at their next scheduled ` +
                  "run, except bots locked on their own. Missed runs are not made up. Pending conditional orders, " +
                  "including ones created during the lock, can be placed within 5 minutes during market hours. The " +
                  "unlock is posted to the Service group.",
            type: "warning",
            confirmButtonText: "Unlock trading",
        };

        this.dialog
            .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, { data })
            .afterClosed()
            .pipe(
                filter((confirmed) => confirmed === true),
                tap(() => this.busy.set(true)),
                switchMap(() =>
                    this.brokerAccountsService.unlockTrading(this.target()).pipe(finalize(() => this.busy.set(false))),
                ),
            )
            .subscribe({
                next: (result) => this.showUnlockResult(result),
                error: () =>
                    this.showFailure(
                        "The unlock request failed, so the lock state is unknown. Unlocking again is safe.",
                    ),
            });
    }

    private showLockResult(result: LockTradingResult): void {
        if (!result.success) {
            this.notificationsService.showError("Not locked", result.error ?? "The lock was refused.");
            return;
        }

        this.setState({ kind: "locked", lockedSinceUtc: result.lockedSinceUtc });

        if (result.wasAlreadyLocked) {
            this.notificationsService.showSuccess("Already locked", "It was already locked. Nothing changed.");
            return;
        }

        this.notificationsService.showSuccess(
            "Trading locked",
            this.bot()
                ? "No new order is placed for this bot until it is unlocked."
                : "No new order is placed for any bot on this broker account until it is unlocked.",
        );
    }

    private showUnlockResult(result: UnlockTradingResult): void {
        this.setState({ kind: "unlocked" });

        if (!result.wasLocked) {
            this.notificationsService.showSuccess("Not locked", "It was not locked. Nothing changed.");
            return;
        }

        this.notificationsService.showSuccess(
            "Trading unlocked",
            this.bot()
                ? "It trades from its next scheduled run, unless its broker account is locked."
                : "Its bots resume at their next scheduled run, except bots locked on their own. Pending conditional " +
                      "orders can be placed within 5 minutes during market hours.",
        );
    }

    private showFailure(message: string): void {
        this.setState({ kind: "unknown" });
        this.notificationsService.showError("Error", message);
    }

    private setState(state: TradingLockState): void {
        this.state.set(state);
        // An answer can come after the user dialog has closed; emitting from a destroyed view only warns (NG0953).
        if (!this.destroyRef.destroyed) {
            this.stateChange.emit(state.kind);
        }
    }
}
